/* OKKO B2B — quick-order table: row subtotals, variant expansion, ONE global add-to-cart,
 * and a PERSISTENT DRAFT ORDER.
 *
 * The quantities are not page state: they live in sessionStorage (per tab), keyed by
 * variant id, with price/max/title alongside. The table is a VIEW over that draft —
 * typing writes the draft, and every AJAX swap, page load or bfcache return repaints
 * the visible rows from it. That is what lets a buyer walk pages 1..N (or leave to the
 * account page and come back) without losing anything.
 *
 * Consequences to keep intact when editing:
 *   - the order total and the add-to-cart button reflect the WHOLE draft, not the page;
 *     lines set on other pages are counted (the note next to the total says how many)
 *   - the global add-to-cart submits the whole draft in one atomic POST
 *   - "Azzera Ordine" empties the draft, not just the visible inputs
 *
 * And the usual warning: Shopify's 422 does NOT say which line failed, and /cart/add.js
 * is ATOMIC. The only way to blame the right row is the pre-send check against /cart.js.
 */

(() => {
  'use strict';

  const EMPTY = '\u2014'; // &mdash;
  const DRAFT_KEY = 'okko-quickorder-draft';
  const DRAFT_TS_KEY = 'okko-quickorder-draft-ts'; // ultima modifica, per la card homepage
  const PASSO_KEY = 'okko-passo-qta'; // quanti pezzi per click su +/- (ingrosso)

  const j = (url, opts) => fetch(url, opts).then((r) => r.json().then((b) => ({ ok: r.ok, body: b })));

  /** Formats cents using the shop's own money_format, handed over by the layout. */
  function money(cents) {
    const fmt = (window.OKKO && window.OKKO.moneyFormat) || '€{{amount}}';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  // --- Draft (sessionStorage) -----------------------------------------------

  function loadDraft() {
    try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || {}; }
    catch { return {}; }
  }

  function saveDraft(draft) {
    try {
      if (Object.keys(draft).length) {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        sessionStorage.setItem(DRAFT_TS_KEY, String(Date.now()));
      } else {
        sessionStorage.removeItem(DRAFT_KEY);
        sessionStorage.removeItem(DRAFT_TS_KEY);
      }
    } catch { /* storage negato o pieno: si degrada a stato per-pagina */ }
  }

  // Confezione (multiplo d'ordine per-prodotto, metafield custom.confezione): 0 = nessun vincolo.
  function confezione(el) {
    const c = Number(el && el.dataset && el.dataset.okkoConfezione);
    return c > 1 ? c : 0;
  }

  /**
   * Clamp della quantita' con confezione (multipli). Regola "solo multipli di N":
   * 0 = non ordinare; >0 => aggancio al multiplo PER DIFETTO (floor: mai consegnare
   * piu' di quanto digitato — decisione di Nicolas, 2026-07-28), mai sotto una
   * confezione (24 su conf.50 => 50: il minimo ordinabile e' una confezione), mai
   * oltre il piu' grande multiplo che sta nello stock (stock<confezione => 0).
   */
  function clampQty(v, max, conf) {
    const c = conf > 1 ? conf : 0;
    const stock = (max === '' || max === null || max === undefined) ? Infinity : Number(max);
    const maxV = c && Number.isFinite(stock) ? Math.floor(stock / c) * c : stock;
    let q = Math.floor(Number(v) || 0);
    if (c && q > 0) q = Math.max(c, Math.floor(q / c) * c); // floor, minimo una confezione
    return Math.max(0, Math.min(maxV, q));
  }
  window.OKKO = window.OKKO || {};
  window.OKKO.clampQty = clampQty;

  // Clamp MORBIDO, per la digitazione: solo pavimento 0 e tetto stock, MAI l'aggancio
  // alla confezione. Agganciare a ogni keystroke rende impossibile digitare (il "7"
  // di "71" verrebbe riportato a 0 prima del secondo tasto): il multiplo si applica
  // sul `change` (blur/Invio) e sugli stepper, vedi sotto.
  function clamp(input) {
    input.value = String(clampQty(input.value, input.max, 0));
  }

  // Aggancio alla confezione a fine digitazione: il valore si arrotonda al multiplo
  // e si rilancia `input` per far ripassare draft/subtotali dall'unico flusso.
  function snapConfezione(input) {
    const c = confezione(input);
    if (!c) return;
    const q = clampQty(input.value, input.max, c);
    if (String(q) === input.value) return;
    input.value = String(q);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Passo della quantita': quanti pezzi aggiunge o toglie un click su +/-.
   * Nell'ingrosso ordinare 50 pezzi a colpi di uno non e' praticabile; il
   * selettore vive nella sidebar (blocco "Passo quantita'") e lo stato in
   * localStorage, cosi' vale su tutte le pagine e sopravvive al reload.
   */
  const passo = () => {
    try { return Math.max(1, Number(localStorage.getItem(PASSO_KEY)) || 1); }
    catch { return 1; }
  };
  window.OKKO = window.OKKO || {};
  window.OKKO.passo = passo;

  const allLines = () => [...document.querySelectorAll('[data-okko-line]')];
  const rowFor = (id) => document.querySelector(`[data-okko-line][data-variant-id="${id}"]`);
  // Le righe sono celle di tabella (catalogo, ricerca, carrello) oppure righe
  // delle modali (.okko-qo-row: modale Più ordinati, server-rendered col
  // contratto bozza): foto in __img, titolo/variante/SKU in __name.
  const CELLA = '.okko-cell-product, .okko-qo-row__name';
  const lineSku = (tr) => tr.querySelector('.okko-cell-sku, .okko-qo-row__sku')?.textContent.trim() || '';
  const lineImg = (tr) => tr.querySelector('.okko-cell-product img, .okko-qo-row__img img')?.currentSrc
    || tr.querySelector('.okko-cell-product img, .okko-qo-row__img img')?.src || '';

  /** Product URL of a line (variant rows borrow it from their parent row). */
  function lineUrl(tr) {
    const own = tr.querySelector('.okko-cell-product a, .okko-qo-row__img a, .okko-qo-row__name a');
    if (own) return own.getAttribute('href') || '';
    const pid = tr.dataset.okkoChildOf;
    const pa = pid && document.querySelector(`[data-okko-parent="${pid}"] .okko-cell-product a`);
    return pa ? pa.getAttribute('href') || '' : '';
  }

  /** Anchor del TITOLO della riga PADRE di una sotto-riga variante del catalogo.
   *  Non il primo <a> della cella: da quando la miniatura e' un link (751f034)
   *  e' quella, vuota, e le voci di bozza nascevano senza titolo («Articolo»
   *  nel Riepilogo). Il ripiego tiene vivo il riconoscimento della sotto-riga. */
  function parentAnchor(tr) {
    const pid = tr.dataset.okkoChildOf;
    const padre = pid && document.querySelector(`[data-okko-parent="${pid}"]`);
    return padre ? padre.querySelector('.okko-cell-product .okko-cell-titolo a') || padre.querySelector('.okko-cell-product a') : null;
  }

  /**
   * Titolo della VARIANTE, quando la riga ne rappresenta una. Viaggia nel campo
   * `v` delle entry e le modali lo rendono come pillola, esattamente come il
   * carrello: prima era concatenato al titolo col trattino lungo.
   */
  function lineVariant(tr) {
    // Righe della PDP (lista-varianti): niente riga-padre, la variante viaggia
    // esplicita sull'attributo. Anche vuota e' autorevole (mono-variante).
    if (tr.dataset.okkoVariant !== undefined) return tr.dataset.okkoVariant;
    const cell = tr.querySelector(CELLA);
    if (!cell) return '';
    // Riga gia' marcata in pagina (carrello, "I tuoi piu' ordinati"): pillola pronta.
    const tag = cell.querySelector('.okko-qo-tag');
    if (tag) return tag.textContent.trim();
    // Sotto-riga del catalogo: il titolo PROPRIO della riga e' la variante.
    return parentAnchor(tr) ? ownTitle(cell) : '';
  }

  /** Titolo scritto NELLA riga, senza risalire al prodotto padre. */
  function ownTitle(cell) {
    // .okko-cell-titolo e' l'elemento canonico del titolo in TUTTE le liste: h3
    // con anchor sulle righe prodotto, span nudo sulle sotto-righe variante.
    // Senza questo ramo le sotto-righe (nessun <a>, nessun nodo di testo diretto)
    // tornavano stringa vuota e la variante non arrivava mai nella bozza.
    const titolo = cell.querySelector('.okko-cell-titolo');
    if (titolo && titolo.textContent.trim()) return titolo.textContent.trim();
    const a = cell.querySelector('a');
    let own = a ? a.textContent.trim() : '';
    if (!own) {
      // :scope > div pescherebbe il div.image (wrapper dell'immagine, vuoto di testo)
      // e il titolo delle varianti uscirebbe troncato ("PRIME — ").
      const div = cell.querySelector(':scope > div:not(.image)') || cell;
      for (const n of div.childNodes) {
        if (n.nodeType === 3 && n.textContent.trim()) { own = n.textContent.trim(); break; }
      }
    }
    return own;
  }

  /** Human title for a line, for errors on rows that are not on the current page. */
  function lineTitle(tr) {
    // Righe della PDP: il titolo PRODOTTO viaggia esplicito sull'attributo
    // (la cella visibile mostra solo il nome variante).
    if (tr.dataset.okkoTitle) return tr.dataset.okkoTitle;
    const cell = tr.querySelector(CELLA);
    if (!cell) return '';
    // Sotto-riga variante: il titolo e' quello del PRODOTTO, la variante sta in `v`.
    const pa = parentAnchor(tr);
    return pa ? pa.textContent.trim() : ownTitle(cell);
  }

  // --- Subtotali e totale ordine --------------------------------------------

  function paintRow(tr) {
    const input = tr.querySelector('[data-okko-qty]');
    const cell = tr.querySelector('[data-okko-subtotal]');
    if (!input || !cell) return;
    const qty = Number(input.value) || 0;
    cell.textContent = qty > 0 ? money(qty * Number(tr.dataset.price)) : EMPTY;
  }

  function updateTotals() {
    const draft = loadDraft();
    let qty = 0;
    let total = 0;
    for (const id in draft) {
      qty += draft[id].q;
      total += draft[id].q * draft[id].p;
    }

    // querySelectorAll: oltre alla barra d'ordine il totale vive anche nella
    // card "La tua bozza d'ordine" della homepage.
    document.querySelectorAll('[data-okko-order-total]').forEach((el) => {
      el.textContent = total > 0 ? money(total) : EMPTY;
    });
    // la nota "IVA escl." qualifica la cifra: a bozza vuota il totale e' un
    // trattino, e un trattino non ha imponibile.
    document.querySelectorAll('[data-okko-order-iva]').forEach((el) => {
      el.hidden = total <= 0;
    });
    // valore di rivendita totale accanto al totale (barra d'ordine)
    const listinoHtml = listinoFraseHtml(Object.values(draft));
    document.querySelectorAll('[data-okko-order-listino]').forEach((el) => {
      el.innerHTML = listinoHtml;
      el.hidden = !listinoHtml;
    });
    // querySelectorAll: il bottone-bozza ora vive anche nella sidebar della shell.
    // Le voci marcate data-okko-draft-fisso restano visibili anche a bozza vuota.
    const linesCount = Object.keys(draft).length;
    document.querySelectorAll('[data-okko-draft-open]').forEach((draftBtn) => {
      if (!draftBtn.hasAttribute('data-okko-draft-fisso')) draftBtn.hidden = linesCount === 0;
      const badge = draftBtn.querySelector('[data-okko-draft-count]');
      if (badge) {
        badge.textContent = String(linesCount);
        badge.hidden = linesCount === 0;
      }
    });

    // Card "La tua bozza d'ordine" (homepage): frase viva conteggio + ultima modifica.
    const vivi = document.querySelectorAll('[data-okko-bozza-vivo]');
    if (vivi.length) {
      const T = (window.OKKO && window.OKKO.testi) || {};
      let frase;
      if (linesCount === 0) {
        frase = T.bozzaVuota || 'Nessun articolo in sospeso.';
      } else {
        const unita = linesCount === 1 ? (T.articolo || 'articolo') : (T.articoli || 'articoli');
        frase = `${linesCount} ${unita} ${T.inSospeso || 'in sospeso'}`;
        let ts = 0;
        try { ts = Number(sessionStorage.getItem(DRAFT_TS_KEY)) || 0; } catch { /* ok */ }
        if (ts) {
          const d = new Date(ts);
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          frase += ` \u2014 ${T.ultimaModifica || 'ultima modifica alle'} ${hh}:${mm}`;
        }
        frase += '.';
      }
      vivi.forEach((el) => { el.textContent = frase; });
    }

    const btn = document.querySelector('[data-okko-add-all]');
    if (btn && !btn.dataset.state) btn.disabled = qty === 0;
    const clearBtn = document.querySelector('[data-okko-clear-qty]');
    if (clearBtn) clearBtn.disabled = qty === 0;

    // Subtotale aggregato sulla riga prodotto: somma delle sue sotto-righe (DOM).
    document.querySelectorAll('[data-okko-parent]').forEach((parent) => {
      const cell = parent.querySelector('[data-okko-parent-subtotal]');
      if (!cell) return;
      let sum = 0;
      let pieces = 0;
      document
        .querySelectorAll(`[data-okko-child-of="${parent.dataset.okkoParent}"]`)
        .forEach((tr) => {
          const input = tr.querySelector('[data-okko-qty]');
          if (!input) return;
          const n = Number(input.value) || 0;
          pieces += n;
          sum += n * Number(tr.dataset.price);
        });
      cell.textContent = sum > 0 ? money(sum) : EMPTY;
      const qtyCell = parent.querySelector('[data-okko-parent-qty]');
      if (qtyCell) qtyCell.textContent = pieces > 0 ? `${pieces} pz` : '';
    });
  }

  function writeDraft(tr, input) {
    const draft = loadDraft();
    const id = tr.dataset.variantId;
    const q = Number(input.value) || 0;
    if (q > 0) {
      draft[id] = {
        q,
        p: Number(tr.dataset.price),
        c: tr.dataset.compare ? Number(tr.dataset.compare) : null,
        op: tr.dataset.okkoBasePrice ? Number(tr.dataset.okkoBasePrice) : null,
        m: input.max === '' ? null : Number(input.max),
        cf: confezione(input), // multiplo d'ordine della riga (0 = libero): serve alle modali
        pre: input.dataset.okkoPreorder || 0, // pre-order: badge In arrivo nelle modali
        t: lineTitle(tr),
        v: lineVariant(tr),
        s: lineSku(tr),
        i: lineImg(tr),
        u: lineUrl(tr),
      };
    } else {
      delete draft[id];
    }
    saveDraft(draft);
  }

  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-okko-qty]')) return;
    clamp(e.target);
    const tr = e.target.closest('[data-okko-line]');
    writeDraft(tr, e.target);
    paintRow(tr);
    updateTotals();
  });

  // Fine digitazione (blur/Invio): aggancio della quantita' al multiplo di confezione.
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-okko-qty]')) return;
    snapConfezione(e.target);
  });

  /**
   * Cambia la quantita' di una riga di bozza da fuori la tabella (modale riepilogo):
   * clamp al max noto, scrittura del draft, sync della riga visibile se presente,
   * totali e badge. Ritorna la quantita' effettiva dopo il clamp.
   */
  function setDraftQty(id, wanted, { snap = true } = {}) {
    const draft = loadDraft();
    const entry = draft[id];
    if (!entry) return 0;
    // snap: aggancio al multiplo di confezione (stepper, blur). Durante la
    // DIGITAZIONE si passa snap:false, o il primo tasto verrebbe riportato a 0.
    const q = clampQty(wanted, entry.m, snap ? (entry.cf || 0) : 0);
    if (q === 0) delete draft[id];
    else entry.q = q;
    saveDraft(draft);
    const tr = rowFor(id);
    if (tr) {
      const input = tr.querySelector('[data-okko-qty]');
      if (input) { input.value = String(q); paintRow(tr); }
    }
    updateTotals();
    return q;
  }

  // Stepper − / + : scrive il valore e rilancia `input`, cosi' clamp/draft/subtotale/totale
  // passano dall'unico flusso qui sopra.
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-okko-qty-step]');
    if (!step) return;
    const input = step.parentElement.querySelector('[data-okko-qty]');
    const inc = confezione(input) || passo(); // confezione: avanza di N per click
    input.value = String((Number(input.value) || 0) + Number(step.dataset.okkoQtyStep) * inc);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    snapConfezione(input); // partenza non-multipla (digitata a meta') -> riaggancia
  });

  // --- Espansione varianti --------------------------------------------------

  function setOpen(parent, open) {
    parent.dataset.open = open ? '1' : '0';
    // la pillola puo' esistere due volte (colonna magazzino + card mobile)
    parent.querySelectorAll('[data-okko-expand]')
      .forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
    document
      .querySelectorAll(`[data-okko-child-of="${parent.dataset.okkoParent}"]`)
      .forEach((tr) => { tr.hidden = !open; });
  }

  document.addEventListener('click', (e) => {
    const parent = e.target.closest('[data-okko-parent]');
    if (!parent || e.target.closest('a')) return;
    setOpen(parent, parent.dataset.open !== '1');
  });

  /** Reveals the row of a blocked line: an error on a collapsed variant must be seen. */
  function reveal(tr) {
    if (!tr.hidden) return;
    const parent = document.querySelector(`[data-okko-parent="${tr.dataset.okkoChildOf}"]`);
    if (parent) setOpen(parent, true);
  }

  // --- Restore: dipinge il draft sulle righe visibili -----------------------
  // Chiamata al load, al ritorno da bfcache e dopo ogni swap AJAX (okko-facets.js).

  function restoreDraft() {
    const draft = loadDraft();
    let dirty = false;

    allLines().forEach((tr) => {
      const id = tr.dataset.variantId;
      const entry = draft[id];
      const input = tr.querySelector('[data-okko-qty]');

      if (!input) {
        // La variante non e' piu' ordinabile (esaurita): via dalla bozza.
        if (entry) { delete draft[id]; dirty = true; }
        return;
      }
      if (!entry) {
        if (input.value !== '0') { input.value = '0'; paintRow(tr); }
        return;
      }

      // Il DOM e' piu' fresco dello storage: riallinea quantita' (clamp allo stock
      // attuale), prezzo, max e titolo.
      const max = input.max === '' ? Infinity : Number(input.max);
      const q = Math.min(entry.q, max);
      if (q === 0) {
        delete draft[id];
        dirty = true;
      } else {
        const freshT = lineTitle(tr);
        // dirty anche sul TITOLO: senza, la riparazione delle entry vecchie restava
        // solo in memoria e la modale rileggeva dallo storage il titolo rotto.
        // Stessa riparazione per la VARIANTE: le entry salvate prima del campo `v`
        // (titolo col trattino) si risanano appena la riga torna in pagina.
        const freshV = lineVariant(tr);
        if (q !== entry.q || entry.p !== Number(tr.dataset.price) || entry.t !== freshT || entry.v !== freshV) dirty = true;
        entry.q = q;
        entry.p = Number(tr.dataset.price);
        entry.c = tr.dataset.compare ? Number(tr.dataset.compare) : null;
        entry.op = tr.dataset.okkoBasePrice ? Number(tr.dataset.okkoBasePrice) : null;
        entry.m = input.max === '' ? null : Number(input.max);
        entry.t = freshT;
        entry.v = freshV;
        entry.s = lineSku(tr);
        entry.i = lineImg(tr) || entry.i || '';
        entry.u = lineUrl(tr) || entry.u || '';
      }
      input.value = String(q);
      paintRow(tr);

      // Una bozza dentro una riga richiusa va resa visibile.
      if (q > 0 && tr.hidden) reveal(tr);
    });

    if (dirty) saveDraft(draft);
    updateTotals();
  }

  // --- Carrello -------------------------------------------------------------

  async function cartQuantities() {
    const { body } = await j('/cart.js');
    const map = new Map();
    (body.items || []).forEach((i) => map.set(i.variant_id, i.quantity));
    return map;
  }

  // Totale del carrello scontato BSS per il DISPLAY (pill di testata), coerente
  // con carrello e drawer. Best-effort SINCRONO: usa i prezzi variante gia' noti
  // al ponte; se i dati di un prodotto non sono ancora caricati usa il pieno per
  // quella riga (si riallinea al prossimo update). BSS assente/senza regole ->
  // total_price invariato.
  function totaleCarrelloDisplay(cart) {
    const bss = window.OKKO && window.OKKO.bss;
    if (!bss || !bss.pronto) return cart.total_price;
    let t = 0;
    (cart.items || []).forEach((item) => {
      const d = bss.prezzoVariante(item.variant_id);
      t += (d ? d.p : item.final_price) * item.quantity;
    });
    return t;
  }

  // --- Soglie carrello (spedizione gratuita / sconti a scaglioni) ------------
  // Blocchi "Soglia" della sezione header (importo € IVA escl. + etichetta),
  // dati nell'embed JSON di testata. Il totale e' lo STESSO della pillola
  // (scontato BSS quando il ponte e' pronto). Aggiorna OGNI istanza presente:
  // il testo del prossimo step in testata E la barra piena nel drawer (che si
  // ricostruisce a ogni render e richiama aggiornaSoglie() senza argomento).
  /** Avanzamento 0-100 con le tacche equidistanti: ogni scaglione vale 1/n
   *  della barra, dentro lo scaglione si interpola sull'importo. */
  function percentualeSoglie(totale, dati) {
    const n = dati.length;
    for (let i = 0; i < n; i += 1) {
      if (totale < dati[i].s) {
        const prec = i === 0 ? 0 : dati[i - 1].s;
        const passo = dati[i].s - prec;
        const dentro = passo > 0 ? Math.max(0, (totale - prec) / passo) : 0;
        return ((i + dentro) * 100) / n;
      }
    }
    return 100;
  }

  let ultimoTotaleSoglie = 0;
  function aggiornaSoglie(totale) {
    if (totale == null) totale = ultimoTotaleSoglie;
    else ultimoTotaleSoglie = totale;
    const embed = document.querySelector('[data-okko-soglie-dati]');
    if (!embed) return;
    let dati;
    try { dati = JSON.parse(embed.textContent); } catch (e) { return; }
    if (!Array.isArray(dati) || !dati.length) return;
    dati.sort((a, b) => a.s - b.s);
    const max = dati[dati.length - 1].s;
    const T = (window.OKKO && window.OKKO.testi) || {};
    const prossima = dati.find((d) => totale < d.s);
    document.querySelectorAll('[data-okko-soglie]').forEach((bar) => {
      // schema colori dell'avviso in testata: base finche' c'e' un obiettivo,
      // quello "completata" quando tutte le soglie sono raggiunte (settings)
      const schemaBase = bar.dataset.okkoSoglieScheme;
      const schemaDone = bar.dataset.okkoSoglieSchemeCompletata;
      if (schemaBase && schemaDone) {
        bar.classList.remove(schemaBase, schemaDone);
        bar.classList.add(prossima ? schemaBase : schemaDone);
      }
      // Riempimento A SEGMENTI: le tacche sono equidistanti (una per soglia),
      // quindi la barra avanza di 1/n per scaglione e dentro lo scaglione va
      // in proporzione. Con la percentuale secca su `max` il fill non sarebbe
      // piu' allineato alle tacche.
      const fill = bar.querySelector('[data-okko-soglie-fill]');
      if (fill && max > 0) fill.style.width = `${percentualeSoglie(totale, dati)}%`;
      // larghezza massima delle etichette = un segmento di track, cosi' le
      // frasi lunghe ("Spedizione gratuita") vanno a capo invece di accavallarsi
      const trackEl = bar.querySelector('.okko-soglie__track');
      if (trackEl && trackEl.offsetWidth) {
        bar.style.setProperty('--okko-soglie-eti', `${Math.floor(trackEl.offsetWidth / dati.length)}px`);
      }
      // progressbar ARIA (drawer): valore corrente in euro + testo leggibile
      const track = bar.querySelector('.okko-soglie__track[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', Math.min(Math.round(totale / 100), Math.round(max / 100)));
        track.setAttribute('aria-valuetext', `${money(totale)} su ${money(max)}`);
      }
      bar.querySelectorAll('[data-okko-soglie-tacca]').forEach((t) => {
        t.classList.toggle('is-passata', totale >= Number(t.dataset.okkoSoglieTacca));
      });
      // frase DISCORSIVA (stile B2C): configurabile PER SOGLIA dal blocco
      // (campo "frase", segnaposto %S%/%V%; vuoto -> template del locale) e a
      // completamento dal setting di sezione. Costruita a nodi (niente
      // innerHTML: i testi arrivano dall'editor), importo e vantaggio in strong.
      const frase = bar.querySelector('[data-okko-soglie-frase]');
      if (frase) {
        frase.textContent = '';
        if (!prossima) {
          const conf = document.querySelector('[data-okko-soglie-completata]');
          frase.textContent = (conf && conf.dataset.okkoSoglieCompletata)
            || T.soglieSbloccati || 'Tutti i vantaggi sbloccati';
        } else {
          const tpl = (prossima.f && prossima.f.trim())
            || T.soglieFrase || 'Ti mancano %S% per ottenere: %V%';
          tpl.split(/(%S%|%V%)/).forEach((pezzo) => {
            if (pezzo === '%S%' || pezzo === '%V%') {
              const b = document.createElement('strong');
              b.textContent = pezzo === '%S%' ? money(prossima.s - totale) : prossima.l;
              frase.appendChild(b);
            } else if (pezzo) {
              frase.appendChild(document.createTextNode(pezzo));
            }
          });
        }
      }
    });
  }
  window.OKKO = window.OKKO || {};
  window.OKKO.aggiornaSoglie = aggiornaSoglie;
  // primo render col totale server (pieno; okko:bss:pronto poi lo sconta via
  // updateCartCount) + re-init quando l'editor ri-renderizza la sezione header
  const initSoglie = () => {
    const bar = document.querySelector('[data-okko-soglie][data-okko-soglie-totale]');
    if (bar) aggiornaSoglie(Number(bar.dataset.okkoSoglieTotale) || 0);
  };
  document.addEventListener('DOMContentLoaded', initSoglie);
  document.addEventListener('shopify:section:load', initSoglie);

  function updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    // testata: la pill mostra il TOTALE del carrello, non il conteggio pezzi
    const totaleDisplay = totaleCarrelloDisplay(cart);
    const bssPronto = !(window.OKKO && window.OKKO.bss) || window.OKKO.bss.pronto;
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = money(totaleDisplay);
      el.hidden = cart.item_count === 0;
      // l'anti-lampo di header.liquid l'aveva resa invisibile in attesa del ponte
      if (el.dataset.okkoAttesa && bssPronto) {
        el.style.visibility = '';
        delete el.dataset.okkoAttesa;
      }
      // firma = stato del carrello per cui vale questo totale scontato: al
      // prossimo load si ridipinge subito, senza passare dal prezzo pieno
      if (bssPronto) {
        const sig = `${el.dataset.cartSig ? el.dataset.cartSig.split(':')[0] : ''}:${cart.item_count}:${cart.total_price}`;
        el.dataset.cartSig = sig;
        try { localStorage.setItem('okko-cart-tot', JSON.stringify({ sig, txt: money(totaleDisplay) })); }
        catch (e) { /* storage negato: nessuna cache, comportamento di prima */ }
      }
    });
    aggiornaSoglie(totaleDisplay); // barra soglie: stesso totale della pillola
    document.dispatchEvent(new CustomEvent('okko:cart:updated', { detail: cart }));
  }

  // La pill di testata e' server-rendered da Liquid al load col totale PIENO;
  // updateCartCount la sconta solo sui cambi JS. Quando il ponte BSS e' pronto
  // (su OGNI pagina con carrello non vuoto) si assicura i dati delle varianti in
  // carrello e ridipinge la pill col totale scontato, coerente con carrello/drawer.
  document.addEventListener('okko:bss:pronto', async () => {
    if (!document.querySelector('[data-cart-total]')) return;
    let cart;
    try { cart = await fetch('/cart.js').then((r) => r.json()); } catch (e) { return; }
    if (!cart || !cart.item_count) return;
    const bss = window.OKKO && window.OKKO.bss;
    if (bss) {
      const pids = [...new Set((cart.items || []).map((i) => i.product_id).filter(Boolean))];
      try { await Promise.all(pids.map((pid) => bss.assicuraProdotto(pid))); } catch (e) { /* pill al pieno */ }
    }
    updateCartCount(cart);
  });

  function clearErrors() {
    document
      .querySelectorAll('[data-okko-modal-error], [data-okko-global-error]')
      .forEach((el) => { el.hidden = true; el.textContent = ''; });
  }

  /**
   * Avvisi di giacenza: si mostrano SOLO nel Riepilogo bozza, sotto la riga del
   * prodotto interessato. Se la modale e' chiusa la apre, poi scorre al primo
   * problema. `globale` e' il messaggio senza riga (es. 422 di /cart/add.js).
   *
   * @param {Array<{id:number, msg:string}>} problemi
   * @param {string} [globale]
   */
  function mostraErroriNelRiepilogo(problemi, globale) {
    const entries = buildDraftModal();
    if (!entries) return;
    if (!draftDialog.open) {
      draftDialog.showModal();
      hydrateDraftImages(entries);
    }
    let prima = null;
    problemi.forEach(({ id, msg }) => {
      const riga = draftDialog.querySelector(`[data-okko-draft-row="${id}"]`);
      const el = riga && riga.querySelector('[data-okko-modal-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
      prima = prima || riga;
    });
    if (globale) {
      const piede = draftDialog.querySelector('.okko-draft-modal__foot');
      if (piede && !piede.querySelector('[data-okko-global-error]')) {
        piede.insertAdjacentHTML('afterbegin', '<p class="okko-foot-error" data-okko-global-error></p>');
      }
      const g = draftDialog.querySelector('[data-okko-global-error]');
      if (g) { g.textContent = globale; g.hidden = false; }
    }
    if (prima) prima.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * @param {Array<{id:number, quantity:number, max:number|null, t:string, scope:Element|null}>} lines
   *   scope e' null per le righe della bozza che non stanno nella pagina corrente:
   *   il loro errore va nel messaggio globale, prefissato dal titolo.
   * @returns {Promise<boolean>} true if the cart actually changed
   */
  async function addLines(lines) {
    const inCart = await cartQuantities();
    let firstBlocked = null;

    const problemi = [];
    for (const line of lines) {
      if (line.max === null) continue;
      const already = inCart.get(line.id) || 0;
      const room = line.max - already;
      if (line.quantity > room) {
        firstBlocked = firstBlocked || line;
        problemi.push({
          id: line.id,
          msg: room <= 0
            ? `Hai già ${already} pezzi nel carrello: è tutta la giacenza disponibile`
            : `Puoi aggiungerne solo ${room}: ${already} già nel carrello`,
        });
      }
    }
    if (firstBlocked) {
      // Gli avvisi vivono SOLO nel Riepilogo: la lista in pagina (catalogo,
      // "I tuoi piu' ordinati") non e' il posto giusto — la riga col problema
      // potrebbe non esserci nemmeno. La modale si apre da sola sul punto.
      mostraErroriNelRiepilogo(problemi);
      return false;
    }

    const { ok, body } = await j('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines.map((l) => ({ id: l.id, quantity: l.quantity })) }),
    });

    if (!ok) {
      // Race only. Shopify won't say which line, so the message has to stay generic.
      mostraErroriNelRiepilogo([], body.description || body.message || 'Aggiunta non riuscita');
      return false;
    }

    const { body: cart } = await j('/cart.js');
    updateCartCount(cart);
    return true;
  }

  // --- Modale riepilogo bozza ------------------------------------------------
  // Costruita dal DRAFT, non dal carrello: mostra cio' che STA PER essere aggiunto.

  let draftDialog = null;

  function ensureDraftDialog() {
    if (draftDialog) return draftDialog;
    // guscio server-rendered dal gruppo Strumenti (okko-strumento-bozza), se c'e'
    draftDialog = document.querySelector('[data-okko-bozza-modal]');
    if (!draftDialog) {
      draftDialog = document.createElement('dialog');
      draftDialog.className = 'okko-draft-modal';
      document.body.appendChild(draftDialog);
    }
    draftDialog.addEventListener('click', (e) => {
      if (e.target === draftDialog || e.target.closest('[data-okko-draft-close]')) draftDialog.close();
    });
    return draftDialog;
  }

  function esc(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // --- Costruttore di riga CONDIVISO dalle tre modali -------------------------
  // Riepilogo, anteprima Quick Order e Wishlist usano LO STESSO markup (classi
  // okko-qo-row): 32px immagine | nome+SKU(+meta) | quantita' | prezzi | azioni.
  // Ogni divergenza di layout passa da qui, non dalle singole modali.

  // Testi dai locales (window.OKKO.testi, emesso da theme.liquid): gestibili da
  // "Default theme content". I fallback tengono in piedi la UI se una chiave manca.
  const T = (window.OKKO && window.OKKO.testi) || {};

  // silhouette PIENA senza dettagli, stesso registro del segnalibro (2026-08-02)
  const TRASH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M9 7V5.4a1.9 1.9 0 0 1 1.9-1.9h2.2A1.9 1.9 0 0 1 15 5.4V7"/><path d="M6.3 7l.8 11.9a2 2 0 0 0 2 1.9h5.8a2 2 0 0 0 2-1.9L17.7 7"/></svg>';
  // Segnalibro "Salvati" (ex cuore wishlist: rebrand B2B del 2026-07-19)
  const SAVE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.5 3.5h11v17l-5.5-3.8-5.5 3.8z"/></svg>';

  function stockBadge(m, pre) {
    const disp = T.disponibile || 'Disponibile';
    const pz = T.pz || 'pz';
    // pre-order (policy continue, giacenza <= 0): `pre` = data '%d/%m' oppure 1.
    // Gemello del ramo is-pre di okko-stock.liquid: tenere allineati i due.
    if (pre) {
      const dal = pre !== 1 && pre !== '1' ? ` · ${T.dal || 'dal'} ${pre}` : '';
      return `<span class="okko-stock is-pre">${T.inArrivo || 'In arrivo'}${dal}</span>`;
    }
    if (m === null || m === undefined) return `<span class="okko-stock is-in">${disp}</span>`;
    if (m === 0) return `<span class="okko-stock is-out">${T.esaurito || 'Esaurito'}</span>`;
    const soglia = (window.OKKO && window.OKKO.stockSoglia) || 20;
    if (m <= soglia) return `<span class="okko-stock is-low">${T.ultimi || 'Ultimi'} ${m} ${pz}</span>`;
    // col numero la parola "Disponibile" e' ridondante (resta solo se manca il numero)
    return `<span class="okko-stock is-in">${m > 100 ? '100+' : m} ${pz}</span>`;
  }

  /**
   * Riga delle modali (Riepilogo, Anteprima QO, Salvati, drawer, dettaglio Ordini).
   * La parte destra ricalca le COLONNE delle liste prodotto del sito:
   * Prezzo | Q.tà | Subtotale | azioni. Slot vuoti = <div></div>, così la
   * griglia resta allineata anche sulle righe morte o senza stepper.
   */
  function modalRow({ attrs = '', imgAttr = '', img = '', url = '', title = '', variante = '', sku = '', stock, pre = 0, metaHtml = '', leadHtml = '', qtyHtml = '<div></div>', priceHtml = '<div></div>', subHtml = '<div></div>', actionsHtml = '<div class="okko-qo-row__azioni"></div>', dead = false, prezzo = null, listino = null }) {
    // okko-cell-titolo = stesso aspetto dei titoli nelle liste (font Titoli)
    const name = `<span class="okko-cell-titolo">${url ? `<a href="${esc(url)}">${esc(title)}</a>` : esc(title)}</span>`;
    // La riga di DEFAULT e' quella dell'anteprima Quick Order: badge stock incluso.
    // `stock` assente del tutto (undefined E metaHtml vuoto) = nessuna riga meta.
    const meta = stock === undefined && !metaHtml
      ? ''
      : `<span class="okko-qo-row__meta">${stock !== undefined ? stockBadge(stock, pre) : ''}${metaHtml}</span>`;
    return `<div class="okko-qo-row${dead ? ' okko-qo-row--dead' : ''}${leadHtml ? ' okko-qo-row--lead' : ''}" ${attrs}>
      ${leadHtml}<div class="okko-qo-row__img" ${imgAttr}>${img ? `<img src="${esc(img)}" alt="" loading="lazy">` : ''}</div>
      <div class="okko-qo-row__name">${name}${sku ? `<span class="okko-qo-row__sku">${esc(sku)}</span>` : ''}${variante ? `<span class="okko-qo-tag">${esc(variante)}</span>` : ''}${meta}</div>
      ${priceHtml}${colonnaRicarico(prezzo, listino)}${colonnaConsigliato(listino)}<div class="okko-qo-row__qta">${qtyHtml}</div>${subHtml}${actionsHtml}
    </div>`;
  }

  // Colonne OPZIONALI delle liste modali (visibili solo se il rispettivo blocco
  // e' attivo nella sezione strumento: classe okko-mod-* sul dialog, pattern
  // dello strumento Ordini). Micro-etichetta sopra il valore: le modali non
  // hanno testata di tabella.
  function colonnaRicarico(prezzo, listino) {
    const iva = Number(window.OKKO.iva) || 22;
    const netto = typeof listino === 'number' ? listino / (1 + iva / 100) : 0;
    // soglia sul valore ARROTONDATO (>= x1,0), come okko-ricarico.liquid: i
    // prezzi a pareggio non devono rendere x1,0 o trattino a caso (2026-08-03)
    const d = typeof prezzo === 'number' && prezzo > 0 && netto > 0
      ? Math.round((netto * 10) / prezzo) : 0;
    // stessa PILLOLA verde del catalogo (span.okko-ricarico, come okko-ricarico.liquid)
    const valore = d >= 10
      ? `<span class="okko-ricarico">\u00d7${Math.floor(d / 10)},${d % 10}</span>`
      : '\u2014';
    return `<div class="okko-qo-row__ricarico">${valore}</div>`;
  }
  function colonnaConsigliato(listino) {
    const valore = typeof listino === 'number' && listino > 0 ? money(listino) : '\u2014';
    return `<div class="okko-qo-row__cons">${valore}</div>`;
  }

  /* Totali di LISTINO di un insieme di righe {q, p, c}, in FRASE \u2014 la STESSA
     ovunque (barra d'ordine, modali bozza/anteprima, drawer, pagina carrello;
     scelta di Nicolas: niente layout a pillola per gli aggregati). P.cons
     totale = \u03a3 q\u00b7c sulle righe che il listino ce l'hanno; ricarico complessivo
     = listino scorporato IVA / spesa sulle STESSE righe (le righe senza listino
     non sporcano il rapporto). Ritorna '' se non c'e' nulla da dire. */
  function listinoFraseHtml(lines) {
    const iva = Number(window.OKKO.iva) || 22;
    let cons = 0;
    let spesa = 0;
    lines.forEach((l) => {
      if (typeof l.c === 'number' && l.c > 0 && l.q > 0) { cons += l.q * l.c; spesa += l.q * l.p; }
    });
    if (!cons || !spesa) return '';
    const netto = cons / (1 + iva / 100);
    const base = (T.listinoFrase || 'Valore di rivendita: %VALORE% IVA inclusa')
      .replace('%VALORE%', `<strong>${money(cons)}</strong>`);
    const coda = netto > spesa
      ? (T.listinoFraseRicarico || ' (ricarico %RICARICO%).')
        .replace('%RICARICO%', `<strong>\u00d7${(netto / spesa).toFixed(1).replace('.', ',')}</strong>`)
      : '.';
    return base + coda;
  }

  function stepperHtml({ qtyAttr, stepAttr, id, value, max, min = 0, conf = 0 }) {
    // conf > 1: la riga si ordina a multipli -> step nativo + data-okko-confezione,
    // cosi' gli handler delle modali (stepper e change) leggono la regola dall'input.
    // L'hint "Confezione da N pz" viaggia nel title (testo da okko.js.confezione_da).
    const c = conf > 1 ? conf : 0;
    const hint = c ? ` title="${((window.OKKO || {}).testi || {}).confezioneDa ? window.OKKO.testi.confezioneDa.replace('%N%', c) : `Confezione da ${c} pz`}"` : '';
    return `<div class="okko-qty">
      <button type="button" data-${stepAttr}="-1" aria-label="Diminuisci" tabindex="-1">&minus;</button>
      <input class="okko-qty-cell" data-${qtyAttr}="${id}" type="number" inputmode="numeric" min="${min}" step="${c || 1}" ${c ? `data-okko-confezione="${c}"` : ''}${hint} ${max !== null && max !== undefined ? `max="${max}"` : ''} value="${value}" aria-label="Quantit\u00e0">
      <button type="button" data-${stepAttr}="1" aria-label="Aumenta" tabindex="-1">+</button>
    </div>`;
  }

  /**
   * Gemello JS dello snippet Liquid okko-prezzo: listino barrato + effettivo +
   * ricarico come moltiplicatore (x2,8). Tenere allineati i due.
   * `originale`: prezzo pieno prima dello sconto personalizzato BSS. Se presente
   * (> prezzo) vince e si rende come nel catalogo \u2014 pieno barrato + scontato,
   * SENZA ricarico (lo sconto B2B ha gia' abbassato il netto).
   */
  function prezzoHtml(prezzo, listino, originale) {
    // `listino` (prezzo CONSIGLIATO al pubblico, metafield) NON e' uno sconto:
    // mai barrato/rosso da li' (decisione di Nicolas, 2026-07-28). Il parametro
    // resta in firma per compatibilita' dei call-site. L'unico barrato e' lo
    // sconto personale BSS (`originale` = prezzo pieno prima della regola).
    if (typeof originale === 'number' && originale > prezzo && prezzo > 0) {
      return `<s class="okko-listino">${money(originale)}</s> `
        + `<span class="okko-prezzo-attuale is-scontato">${money(prezzo)}</span>`;
    }
    return `<span class="okko-prezzo-attuale">${money(prezzo)}</span>`;
  }

  function prezzoCell(unit, listino, originale) {
    return `<div class="okko-qo-row__prezzo is-num">${prezzoHtml(unit, listino, originale)}</div>`;
  }

  /** Cella Subtotale della riga modale (colonna Subtot. delle liste). */
  function subCell(sub, subAttr = '', id = '') {
    const subText = typeof sub === 'number' ? money(sub) : sub;
    return `<div class="okko-qo-row__sub is-num" ${subAttr ? `data-${subAttr}="${id}"` : ''}>${subText}</div>`;
  }

  /** (Ri)costruisce il contenuto della modale dallo stato corrente della bozza. */
  function buildDraftModal() {
    const draft = loadDraft();
    const entries = Object.entries(draft);
    // voci senza listino (salvate prima di data-compare): completale e ridisegna
    window.OKKO.completaListini(draft).then((cambiato) => {
      if (!cambiato) return;
      saveDraft(draft);
      buildDraftModal();
    });
    const d = ensureDraftDialog();
    if (!entries.length) {
      // editor del tema: la modale resta visibile con lo stato vuoto (anteprima)
      if (window.Shopify && window.Shopify.designMode) {
        d.innerHTML = `
          <div class="okko-draft-modal__head">
            <h2 class="okko-draft-modal__title">${T.riepilogo || 'Riepilogo'}</h2>
            <button type="button" class="okko-draft-modal__close" data-okko-draft-close aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
          </div>
          <div class="okko-draft-modal__body"><p class="okko-ordini__vuoto">${T.bozzaVuota || 'La bozza è vuota.'}</p></div>`;
        return entries;
      }
      d.close();
      return null;
    }

    let qty = 0;
    let total = 0;
    // Stato Salvati per il segnalibro di riga: STESSA chiave di okko-wishlist.js
    let wl = {};
    try { wl = JSON.parse(localStorage.getItem('okko-wishlist')) || {}; } catch { /* ok */ }
    const WISH_X = '<svg class="okko-wish__x" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg>';
    const rows = entries
      .map(([id, en]) => {
        qty += en.q;
        total += en.q * en.p;
        return modalRow({
          attrs: `data-okko-draft-row="${id}"`,
          imgAttr: `data-okko-draft-img="${id}"`,
          img: en.i,
          url: en.u,
          title: en.t || 'Articolo',
          variante: en.v,
          sku: en.s,
          stock: en.m,
          pre: en.pre || 0,
          metaHtml: `<span class="okko-line-error" data-okko-modal-error hidden></span>`,
          prezzo: en.p, listino: en.c,
          priceHtml: prezzoCell(en.p, en.c, en.op),
          qtyHtml: stepperHtml({ qtyAttr: 'okko-modal-qty', stepAttr: 'okko-modal-step', id, value: en.q, max: en.m, min: 0, conf: en.cf }),
          subHtml: subCell(en.q * en.p, 'okko-modal-sub', id),
          leadHtml: `<button type="button" class="okko-wish color-scheme-6${wl[id] ? ' is-on' : ''}" data-okko-wl-toggle="${id}" aria-label="${T.salvaProdotto || 'Salva'}"><svg class="okko-wish__segno" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.5 3.5h11v17l-5.5-3.8-5.5 3.8z"/></svg>${WISH_X}</button>`,
          actionsHtml: `<button type="button" class="okko-draft-row__remove" data-okko-modal-remove="${id}" aria-label="${T.rimuoviSelezione || 'Rimuovi dalla selezione'}">${TRASH_SVG}</button>`,
        });
      })
      .join('');

    d.innerHTML = `
      <div class="okko-draft-modal__head">
        <h2 class="okko-draft-modal__title">
          ${T.riepilogo || 'Riepilogo'}
          <small data-okko-modal-count>${entries.length} ${entries.length === 1 ? (T.articolo || 'articolo') : (T.articoli || 'articoli')} \u00b7 ${qty} ${T.pz || 'pz'}</small>
        </h2>
        <button type="button" class="okko-draft-modal__close" data-okko-draft-close aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
      </div>
      <div class="okko-draft-modal__body">${rows}</div>
      <div class="okko-draft-modal__foot okko-draft-modal__foot--bar">
        <span class="okko-order-total">${T.totale || 'Totale:'} <strong data-okko-modal-total>${money(total)}</strong> <small class="okko-nota-iva">${T.ivaEscl || 'IVA escl.'}</small></span>
        <button type="button" class="okko-btn" data-okko-add-all>${T.aggiungiTutto || 'Aggiungi tutto al carrello'}</button>
        <button type="button" class="okko-btn okko-btn--ghost okko-btn--clear" data-okko-clear-qty>${TRASH_SVG} ${T.azzera || 'Azzera'}</button>
        <span class="okko-order-listino okko-order-listino--riga" data-okko-modal-listino>${listinoFraseHtml(entries.map(([, en]) => en))}</span>
      </div>`;
    return entries;
  }

  /** Aggiorna in place testata e totale della modale (usato durante la digitazione). */
  function refreshModalTotals() {
    if (!draftDialog || !draftDialog.open) return;
    const draft = loadDraft();
    let qty = 0;
    let total = 0;
    let count = 0;
    for (const id in draft) { count += 1; qty += draft[id].q; total += draft[id].q * draft[id].p; }
    const countEl = draftDialog.querySelector('[data-okko-modal-count]');
    if (countEl) countEl.textContent = `${count} ${count === 1 ? (T.articolo || 'articolo') : (T.articoli || 'articoli')} \u00b7 ${qty} ${T.pz || 'pz'}`;
    const totalEl = draftDialog.querySelector('[data-okko-modal-total]');
    if (totalEl) totalEl.textContent = money(total);
    const listinoEl = draftDialog.querySelector('[data-okko-modal-listino]');
    if (listinoEl) listinoEl.innerHTML = listinoFraseHtml(Object.values(draft));
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-okko-draft-open]')) return;
    const entries = buildDraftModal();
    if (!entries) return;
    draftDialog.showModal();
    hydrateDraftImages(entries);
  });

  // --- Strumento "I miei ordini": modale + riordino in bozza -----------------
  // La modale e' server-rendered dalla sezione okko-strumento-ordini (gruppo
  // Strumenti). Le COLONNE della tabella sono blocchi annidati (col-ordine-*):
  // come per la Lista varianti, i figli rendono solo le <th> e qui montiamo le
  // celle di ogni riga-ordine da thead + embed {meta, righe}. Le righe sono
  // gia' nella forma della bozza, con disp/m sullo stato ATTUALE.

  function montaColonneOrdini() {
    const wrap = document.querySelector('[data-okko-ordini-tabella]');
    if (!wrap) return;
    const cols = [...wrap.querySelectorAll('thead th[data-okko-ocol]')];
    wrap.querySelectorAll('tbody tr[data-okko-ordine-riga]').forEach((tr) => {
      tr.querySelectorAll('td[data-okko-ocella]').forEach((td) => td.remove());
      const embed = document.querySelector(`[data-okko-riordina-dati="${tr.dataset.okkoOrdineRiga}"]`);
      if (!embed) return;
      let meta;
      try { meta = JSON.parse(embed.textContent).meta; } catch { return; }
      if (!meta) return;
      cols.forEach((th) => {
        const td = document.createElement('td');
        // la chiave colonna finisce sull'attributo: e' l'ancora del layout a
        // card del mobile (come data-okko-colonna nelle liste prodotto)
        td.dataset.okkoOcella = th.dataset.okkoOcol;
        switch (th.dataset.okkoOcol) {
          case 'data': td.textContent = meta.data; break;
          case 'pagamento': td.innerHTML = `<span class="okko-stato okko-stato--${meta.fin}">${meta.finTesto}</span>`; break;
          case 'evasione': td.innerHTML = `<span class="okko-stato okko-stato--${meta.ful}">${meta.fulTesto}</span>`; break;
          case 'righe': td.textContent = String(meta.righe); break;
          case 'pezzi': td.textContent = `${meta.pezzi} ${T.pz || 'pz'}`; break;
          case 'totale': td.className = 'is-num'; td.innerHTML = `<span class="okko-prezzo">${money(meta.totale)}</span>`; break;
          case 'metafield':
            td.className = 'okko-cell-mf';
            td.textContent = (meta.mf && meta.mf[th.dataset.okkoChiave]) || '';
            break;
          case 'azioni':
            // occhio = apre/chiude il DETTAGLIO (tooltip nativo via title)
            td.className = 'okko-ordine__az';
            td.innerHTML = `<button class="okko-icona-btn" type="button" data-okko-oexp aria-expanded="false" title="${esc(T.vediOrdine || 'Visualizza ordine')}" aria-label="${esc(T.vediOrdine || 'Visualizza ordine')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/></svg></button><button class="okko-btn okko-btn--sm" type="button" data-okko-riordina="${tr.dataset.okkoOrdineRiga}">${T.riordinaBreve || 'Riordina'}</button>`;
            break;
        }
        tr.appendChild(td);
      });
      const det = tr.nextElementSibling;
      if (det && det.hasAttribute('data-okko-ordine-dettaglio')) {
        det.querySelector('td').colSpan = cols.length + 1;
      }
    });
  }
  montaColonneOrdini();
  document.addEventListener('shopify:section:load', montaColonneOrdini);

  /* --- Strumento "I tuoi più ordinati" (sections/okko-strumento-piu-ordinati) ---
     Le righe sono server-rendered nel dialog dal caricamento: all'apertura si
     ridipingono (la bozza puo' essere cambiata dalla pagina). Piede col solo
     "Chiudi", come i Salvati. Alla chiusura (X, Chiudi, velo o Esc) si
     ridipingono le righe della pagina. */
  function apriPiuOrdinati(dlg) {
    restoreDraft();
    dlg.showModal();
    if (window.OKKO.paintWishlist) window.OKKO.paintWishlist();
    if (window.OKKO.bss && window.OKKO.bss.aggiorna) window.OKKO.bss.aggiorna();
  }
  // 'close' non risale: lo si prende in cattura (copre anche Esc)
  document.addEventListener('close', (e) => {
    const dlg = e.target;
    if (!dlg.matches || !dlg.matches('[data-okko-piuord-modal]')) return;
    restoreDraft();
  }, true);

  document.addEventListener('click', (e) => {
    const apriPo = e.target.closest('[data-okko-piuord-apri]');
    if (apriPo) {
      const dlg = document.querySelector('[data-okko-piuord-modal]');
      if (dlg && !dlg.open) { e.preventDefault(); apriPiuOrdinati(dlg); }
      return;
    }
    const dlgPo = document.querySelector('[data-okko-piuord-modal]');
    if (dlgPo && dlgPo.open && (e.target === dlgPo || e.target.closest('[data-okko-piuord-chiudi]'))) {
      dlgPo.close();
    }
  });

  document.addEventListener('click', (e) => {
    // espansione righe dell'ordine (riga colspan nascosta sotto l'ancora)
    const exp = e.target.closest('[data-okko-oexp]');
    if (exp) {
      const tr = exp.closest('tr');
      const det = tr && tr.nextElementSibling;
      if (det && det.hasAttribute('data-okko-ordine-dettaglio')) {
        det.hidden = !det.hidden;
        exp.setAttribute('aria-expanded', String(!det.hidden));
        tr.classList.toggle('is-aperta', !det.hidden);
      }
      return;
    }
    const apri = e.target.closest('[data-okko-ordini-apri]');
    if (apri) {
      const dlg = document.querySelector('[data-okko-ordini-modal]');
      if (dlg) {
        e.preventDefault();
        // aperto da DENTRO un'altra modale ("Storico ordini" dei Più ordinati):
        // quella si chiude, non resta sotto (Nicolas, 2026-10-07). Il suo
        // evento close riporta a casa anche la barra d'ordine.
        const daModale = apri.closest('dialog[open]');
        if (daModale && daModale !== dlg) daModale.close();
        dlg.showModal();
      }
      // senza modale (ospite): il link naviga normalmente al login
      return;
    }

    const dlg = document.querySelector('[data-okko-ordini-modal]');
    if (dlg && dlg.open && (e.target === dlg || e.target.closest('[data-okko-ordini-chiudi]'))) {
      dlg.close();
      return;
    }

    const btn = e.target.closest('[data-okko-riordina]');
    if (!btn || btn.dataset.state) return;
    const embed = document.querySelector(`[data-okko-riordina-dati="${btn.dataset.okkoRiordina}"]`);
    if (!embed) return;
    let righe = [];
    try {
      const dati = JSON.parse(embed.textContent);
      righe = Array.isArray(dati) ? dati : dati.righe || [];
    } catch { return; }
    if (!righe.length) return;

    // Flusso principale: ANTEPRIMA dell'Ordine Rapido con le righe dell'ordine
    // (revisione di esauriti/eliminati/clamp prima della bozza, come l'import)
    if (window.OKKO.qoPreview) {
      if (dlg) dlg.close();
      window.OKKO.qoPreview(righe);
      return;
    }

    // Fallback (Ordine Rapido assente): merge diretto in bozza
    const draft = loadDraft();
    let saltate = 0;
    righe.forEach((r) => {
      if (!r.disp || !r.id) { saltate += 1; return; }
      const gia = draft[r.id] ? draft[r.id].q : 0;
      const max = typeof r.m === 'number' ? r.m : Infinity;
      const q = Math.min(gia + r.q, max);
      if (q <= 0) { saltate += 1; return; }
      draft[r.id] = { q, p: r.p, m: typeof r.m === 'number' ? r.m : null, t: r.t, v: r.v || '', s: r.s, i: r.i, u: r.u };
    });
    saveDraft(draft);
    restoreDraft(); // riallinea gli input della pagina corrente
    updateTotals();

    btn.dataset.state = 'added';
    const testo = btn.textContent;
    btn.textContent = saltate > 0
      ? `${T.inBozza || 'In bozza ✓'} · ${saltate} ${T.nonDisponibili || 'non disponibili'}`
      : (T.inBozza || 'In bozza ✓');
    setTimeout(() => {
      delete btn.dataset.state;
      btn.textContent = testo;
      if (dlg) dlg.close();
      const entries = buildDraftModal();
      if (entries) {
        draftDialog.showModal();
        hydrateDraftImages(entries);
      }
    }, saltate > 0 ? 1800 : 700);
  });

  // Stepper e rimozione nella modale: ricostruzione completa (nessun focus da preservare).
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-okko-modal-step]');
    if (step) {
      const input = step.parentElement.querySelector('[data-okko-modal-qty]');
      const id = input.dataset.okkoModalQty;
      const inc = confezione(input) || passo(); // confezione: avanza di N per click
      setDraftQty(id, (Number(input.value) || 0) + Number(step.dataset.okkoModalStep) * inc);
      const entries = buildDraftModal();
      if (entries) hydrateDraftImages(entries);
      return;
    }
    const remove = e.target.closest('[data-okko-modal-remove]');
    if (remove) {
      setDraftQty(remove.dataset.okkoModalRemove, 0);
      const entries = buildDraftModal();
      if (entries) hydrateDraftImages(entries);
    }
  });

  // Digitazione nella modale: aggiornamento in place (la ricostruzione ruberebbe il focus).
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-okko-modal-qty]')) return;
    const id = e.target.dataset.okkoModalQty;
    const q = setDraftQty(id, e.target.value, { snap: false }); // mentre si digita: solo tetto stock
    e.target.value = String(q);
    // l'avviso di giacenza si riferiva alla quantita' precedente
    const avviso = draftDialog.querySelector(`[data-okko-draft-row="${id}"] [data-okko-modal-error]`);
    if (avviso) { avviso.hidden = true; avviso.textContent = ''; }
    const sub = draftDialog.querySelector(`[data-okko-modal-sub="${id}"]`);
    const draft = loadDraft();
    if (sub) sub.textContent = draft[id] ? money(draft[id].q * draft[id].p) : EMPTY;
    refreshModalTotals();
  });

  // A fine digitazione: aggancio alla confezione, poi le righe a zero escono dalla lista.
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-okko-modal-qty]')) return;
    setDraftQty(e.target.dataset.okkoModalQty, e.target.value); // snap al multiplo
    const entries = buildDraftModal();
    if (entries) hydrateDraftImages(entries);
  });


  /**
   * Best-effort: le righe di bozza senza immagine (scritte da una versione precedente,
   * o su pagine mai rivisitate) la recuperano da /products/<handle>.js all'apertura
   * della modale, aggiornando anche il draft per le prossime volte.
   */
  async function hydrateDraftImages(entries) {
    const missing = entries.filter(([, en]) => !en.i && en.u);
    if (!missing.length) return;
    const draft = loadDraft();
    await Promise.all(missing.map(async ([id, en]) => {
      try {
        const res = await fetch(en.u.split('?')[0] + '.js');
        if (!res.ok) return;
        const prod = await res.json();
        const v = (prod.variants || []).find((x) => String(x.id) === String(id));
        const img = (v && v.featured_image && v.featured_image.src)
          || prod.featured_image
          || (prod.images && prod.images[0])
          || '';
        if (!img) return;
        if (draft[id]) draft[id].i = img;
        const slot = draftDialog && draftDialog.querySelector(`[data-okko-draft-img="${id}"]`);
        if (slot) slot.innerHTML = `<img src="${esc(img)}" alt="" loading="lazy">`;
      } catch { /* niente immagine: resta il vuoto */ }
    }));
    saveDraft(draft);
  }

  // --- Azzera Ordine: svuota la bozza, non solo la pagina --------------------

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-okko-clear-qty]')) return;
    clearErrors();
    saveDraft({});
    allLines().forEach((tr) => {
      const input = tr.querySelector('[data-okko-qty]');
      if (!input || input.value === '0') return;
      input.value = '0';
      paintRow(tr);
    });
    updateTotals();
  });

  // --- ATC globale: invia TUTTA la bozza ------------------------------------

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-okko-add-all]');
    if (!btn) return;

    clearErrors();
    const draft = loadDraft();
    const lines = Object.entries(draft).map(([id, entry]) => ({
      id: Number(id),
      quantity: entry.q,
      max: entry.m,
      t: entry.t || '',
      scope: rowFor(id),
    }));
    if (!lines.length) return;

    btn.disabled = true;
    const label = btn.textContent;
    try {
      if (await addLines(lines)) {
        // L'aggiunta e' atomica: o entra tutta la bozza o niente. Entrata -> bozza vuota.
        saveDraft({});
        lines.forEach((l) => {
          if (!l.scope) return;
          const input = l.scope.querySelector('[data-okko-qty]');
          if (input) { input.value = '0'; paintRow(l.scope); }
        });
        btn.dataset.state = 'added';
        btn.textContent = 'Aggiunto \u2713';
        setTimeout(() => {
          delete btn.dataset.state;
          btn.textContent = label;
          updateTotals();
        }, 2000);
        // aggiunta riuscita -> si apre il drawer carrello (barra E modale
        // Riepilogo: stesso handler), l'esito si vede senza cercare la pill.
        if (window.OKKO && window.OKKO.apriDrawer) window.OKKO.apriDrawer();
      }
      updateTotals();
    } finally {
      if (!btn.dataset.state) btn.disabled = false;
    }
  });

  // --- ATC AJAX globale ------------------------------------------------------
  // Qualsiasi form del tema che punta a /cart/add (es. la pagina prodotto Skeleton)
  // viene intercettato: niente redirect al carrello, solo contatore + feedback sul
  // bottone. Il payment_button dinamico di Shopify ha il suo flusso e non passa di qui.

  const btnLabel = {
    get: (btn) => (btn.tagName === 'INPUT' ? btn.value : btn.textContent),
    set: (btn, text) => { if (btn.tagName === 'INPUT') btn.value = text; else btn.textContent = text; },
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    e.preventDefault();

    const btn = form.querySelector('input[type="submit"], button[type="submit"], button:not([type])');
    const label = btn ? btnLabel.get(btn) : '';
    let errEl = form.querySelector('[data-okko-form-error]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    if (btn) btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const body = await res.json();

      if (!res.ok) {
        if (!errEl) {
          errEl = document.createElement('p');
          errEl.setAttribute('data-okko-form-error', '');
          errEl.className = 'okko-line-error';
          form.appendChild(errEl);
        }
        errEl.textContent = body.description || body.message || 'Aggiunta non riuscita';
        errEl.hidden = false;
        return;
      }

      const { body: cart } = await j('/cart.js');
      updateCartCount(cart);
      if (btn) {
        btn.dataset.state = 'added';
        btnLabel.set(btn, 'Aggiunto \u2713');
        setTimeout(() => {
          delete btn.dataset.state;
          btnLabel.set(btn, label);
          btn.disabled = false;
        }, 2000);
      }
    } catch (err) {
      console.error('[okko-cart]', err);
      form.submit(); // rete ko: degrada al submit classico
    } finally {
      if (btn && !btn.dataset.state) btn.disabled = false;
    }
  });

  // I bottoni azione nel footer del Riepilogo sono GLI STESSI della fascia sticky
  // (stessi data-attribute -> stessi handler qui sopra): questo listener chiude solo
  // la modale, cosi' esiti ed eventuali errori di giacenza restano visibili in pagina.
  document.addEventListener('click', (e) => {
    if (!draftDialog || !draftDialog.open || !draftDialog.contains(e.target)) return;
    if (e.target.closest('[data-okko-add-all], [data-okko-clear-qty]')) draftDialog.close();
  });

  window.OKKO = window.OKKO || {};
  window.OKKO.restoreDraft = restoreDraft;
  // Entry di bozza per id variante (il toggle Salvati del Riepilogo la usa)
  window.OKKO.draftEntry = (id) => loadDraft()[id] || null;

  /**
   * Recupero del LISTINO per le voci salvate senza (bozza e Salvati creati prima
   * che le righe emettessero data-compare, o da superfici che non lo espongono).
   * Prima prova la riga in pagina, poi il prodotto via /products/<handle>.js —
   * una fetch per prodotto, in cache. Chi chiama ridisegna quando la promise
   * si risolve: la modale si apre subito e il barrato compare appena arriva.
   *
   * @param {object} voci - mappa id -> entry (bozza o wishlist)
   * @returns {Promise<boolean>} true se almeno una voce e' stata completata
   */
  const cacheProdotti = new Map();
  window.OKKO.completaListini = async (voci) => {
    // Due campi arrivati dopo: `c` (listino) e `v` (variante). Stessa strategia e
    // stessa fetch, cosi' le voci salvate prima si risanano invece di restare
    // senza barrato o senza pillola.
    // `v` vuoto + riga in pagina: si ricontrolla comunque (costa zero fetch) — cosi'
    // guariscono anche le voci scritte quando la cattura della variante era rotta.
    const mancanti = Object.entries(voci).filter(([id, en]) => en
      && !en.g // i GRUPPI (genitore + vs) non hanno listino/variante propri
      && (en.c === undefined || en.v === undefined || (en.v === '' && rowFor(id))));
    if (!mancanti.length) return false;
    let cambiato = false;
    await Promise.all(mancanti.map(async ([id, en]) => {
      // `cambiato` va alzato SOLO su un valore davvero nuovo: chi chiama risalva e
      // ridisegna, e un true a vuoto su una voce senza variante (v resta '') lo
      // rimetterebbe in ciclo all'infinito.
      const setC = (val) => { if (en.c === undefined) { en.c = val; cambiato = true; } };
      const setV = (val) => {
        if (val) { if (en.v !== val) { en.v = val; cambiato = true; } }
        else if (en.v === undefined) { en.v = ''; cambiato = true; }
      };
      const tr = rowFor(id);
      if (tr && tr.dataset.compare !== undefined) {
        setC(tr.dataset.compare ? Number(tr.dataset.compare) : null);
        setV(lineVariant(tr));
        return;
      }
      if (!en.u) { setC(null); setV(''); return; }
      const handle = String(en.u).split('/products/')[1];
      if (!handle) { setC(null); setV(''); return; }
      const url = `/products/${handle.split('?')[0]}.js`;
      if (!cacheProdotti.has(url)) {
        cacheProdotti.set(url, fetch(url).then((r) => r.json()).catch(() => null));
      }
      const prod = await cacheProdotti.get(url);
      const v = prod && (prod.variants || []).find((x) => String(x.id) === String(id));
      setC(v && v.compare_at_price ? Number(v.compare_at_price) : null);
      setV(v && v.title && v.title !== 'Default Title' ? v.title : '');
    }));
    return cambiato;
  };
  window.OKKO.money = money;
  window.OKKO.updateCartCount = updateCartCount;
  // --- Lightbox CONDIVISA (galleria PDP + guida Ordine Rapido) ---------------
  // window.OKKO.lightbox(items, idx, onNav). Un item e' una IMMAGINE
  // ({full, d?}) oppure un VIDEO: `v` = sorgenti caricate su Shopify
  // ([{u, m}]) o `e` = URL di embed YouTube/Vimeo, con `thumb` come poster.
  // Dialog proprio: showModal chiamata DOPO un'altra modale la mette sopra nel
  // top layer. onNav (opzionale) riceve l'indice a ogni cambio: la PDP ci
  // sincronizza la galleria sotto.
  let lbDialog = null;
  let lbItems = [];
  let lbIdx = 0;
  let lbOnNav = null;

  async function lbVai(delta) {
    const n = lbItems.length;
    lbIdx = ((lbIdx + delta) % n + n) % n;
    const atteso = lbIdx;
    // il media corrente resta a schermo finche' il prossimo non e' decodificato
    await lbPrecarica(lbItems[lbIdx]);
    if (lbIdx !== atteso || !lbDialog || !lbDialog.open) return; // click rapidi: vince l'ultimo
    lbRender();
    if (lbOnNav) lbOnNav(lbIdx);
  }

  /** Il media dell'item: <video> per i file caricati su Shopify, <iframe> per
   *  YouTube/Vimeo, altrimenti l'immagine. */
  function lbMedia(it) {
    if (it.v && it.v.length) {
      const sorgenti = it.v.map((s) => `<source src="${esc(s.u)}" type="${esc(s.m || '')}">`).join('');
      return `<video class="okko-lb__video" controls playsinline preload="metadata"${it.thumb ? ` poster="${esc(it.thumb)}"` : ''}>${sorgenti}</video>`;
    }
    if (it.e) {
      return `<div class="okko-lb__embed"><iframe src="${esc(it.e)}" title="${esc(it.d || 'Video')}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe></div>`;
    }
    return `<img src="${esc(it.full)}" alt="${esc(it.d || '')}">`;
  }

  /** URL dell'immagine che dara' la misura al riquadro (poster per i video). */
  function lbAnteprima(it) {
    if (it.e) return null;                       // l'iframe ha gia' un aspetto fisso da CSS
    return (it.v && it.v.length) ? it.thumb : it.full;
  }

  /** Decodifica l'immagine PRIMA di metterla nel DOM: senza, il markup nuovo
   *  entra con dimensione zero e la lightbox collassa e riscatta a ogni cambio
   *  (visto da Nicolas). Fallisce in silenzio: al peggio si torna al vecchio
   *  comportamento, protetto comunque dal blocco di misura in lbRender. */
  async function lbPrecarica(it) {
    const url = lbAnteprima(it);
    if (!url) return;
    try { const im = new Image(); im.src = url; await im.decode(); } catch (e) { /* ok */ }
  }

  function lbRender() {
    const it = lbItems[lbIdx];
    if (!it || !lbDialog) return;
    // Rete lenta o decode fallito: si blocca la misura del riquadro a quella
    // corrente finche' il nuovo media non e' pronto, cosi' non si stringe mai.
    const media = lbDialog.querySelector('.okko-lb__figura img, .okko-lb__video, .okko-lb__embed');
    const prima = media ? media.getBoundingClientRect() : null;
    const frecce = lbItems.length > 1;
    const caption = (it.d || frecce)
      ? `<figcaption>${it.d ? esc(it.d) : ''}${frecce ? `<span class="okko-lb__contatore">${lbIdx + 1} / ${lbItems.length}</span>` : ''}</figcaption>`
      : '';
    lbDialog.innerHTML = `
      <button type="button" class="okko-draft-modal__close" data-okko-lb-chiudi aria-label="Chiudi"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg></button>
      ${frecce ? '<button type="button" class="okko-lb__freccia okko-lb__freccia--prev" data-okko-lb-vai="-1" aria-label="Precedente"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3.5L5.5 8l4.5 4.5"/></svg></button>' : ''}
      <figure class="okko-lb__figura">
        ${lbMedia(it)}
        ${caption}
      </figure>
      ${frecce ? '<button type="button" class="okko-lb__freccia okko-lb__freccia--next" data-okko-lb-vai="1" aria-label="Successiva"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3.5L10.5 8 6 12.5"/></svg></button>' : ''}`;

    const fig = lbDialog.querySelector('.okko-lb__figura');
    const nuovo = fig && fig.querySelector('img, video, .okko-lb__embed');
    if (!fig || !nuovo) return;
    if (prima && prima.width) {
      fig.style.minWidth = `${Math.round(prima.width)}px`;
      fig.style.minHeight = `${Math.round(prima.height)}px`;
    }
    const libera = () => { fig.style.minWidth = ''; fig.style.minHeight = ''; };
    if (nuovo.tagName === 'IMG') {
      if (nuovo.complete && nuovo.naturalWidth) libera();
      else {
        nuovo.addEventListener('load', libera, { once: true });
        nuovo.addEventListener('error', libera, { once: true });
      }
    } else {
      libera(); // video e iframe hanno gia' misura propria dal CSS
    }
  }

  function lightbox(items, idx, onNav) {
    if (!Array.isArray(items) || !items.length) return;
    lbItems = items;
    lbIdx = Math.max(0, Math.min(Number(idx) || 0, items.length - 1));
    lbOnNav = onNav || null;
    if (!lbDialog) {
      lbDialog = document.createElement('dialog');
      lbDialog.className = 'okko-lightbox';
      document.body.appendChild(lbDialog);
      lbDialog.addEventListener('click', (e) => {
        if (e.target === lbDialog || e.target.closest('[data-okko-lb-chiudi]')) { lbDialog.close(); return; }
        const vai = e.target.closest('[data-okko-lb-vai]');
        if (vai) lbVai(Number(vai.dataset.okkoLbVai));
      });
      lbDialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') lbVai(-1);
        if (e.key === 'ArrowRight') lbVai(1);
      });
      // Chiudendo (X, backdrop o Esc) il contenuto si svuota: senza, un video
      // continuerebbe a suonare a lightbox chiusa. Alla riapertura si ridisegna.
      lbDialog.addEventListener('close', () => { lbDialog.innerHTML = ''; });
    }
    // Prima apertura: si prova a decodificare l'immagine prima di aprire, cosi'
    // il riquadro nasce gia' della misura giusta. Corsa con 250ms: se la rete e'
    // lenta si apre lo stesso, meglio un'immagine che arriva dopo di un click
    // che sembra non aver fatto niente.
    Promise.race([lbPrecarica(lbItems[lbIdx]), new Promise((r) => setTimeout(r, 250))])
      .then(() => {
        lbRender();
        if (!lbDialog.open) lbDialog.showModal();
      });
  }
  window.OKKO.lightbox = lightbox;

  window.OKKO.ui = { modalRow, stepperHtml, prezzoCell, subCell, prezzoHtml, stockBadge, listinoFraseHtml, colonnaRicarico, TRASH_SVG, SAVE_SVG };

  /** Entry-dati di una riga tabella (usata dalla wishlist): stessi campi della bozza. */
  window.OKKO.lineEntry = (tr) => {
    const input = tr.querySelector('[data-okko-qty]');
    return {
      id: Number(tr.dataset.variantId),
      pid: Number(tr.dataset.okkoProductId || tr.dataset.okkoChildOf) || null, // id PRODOTTO: la wishlist lo usa per risolvere lo sconto BSS da sola
      p: Number(tr.dataset.price),
      c: tr.dataset.compare ? Number(tr.dataset.compare) : null,
      op: tr.dataset.okkoBasePrice ? Number(tr.dataset.okkoBasePrice) : null,
      m: input ? (input.max === '' ? null : Number(input.max)) : 0,
      cf: input ? confezione(input) : 0, // confezione: l'input server-rendered (okko-qty-input) e' la fonte
      pre: input ? (input.dataset.okkoPreorder || 0) : 0, // pre-order: data '%d/%m' o '1', come sopra
      t: lineTitle(tr),
      v: lineVariant(tr),
      s: lineSku(tr),
      i: lineImg(tr),
      u: lineUrl(tr),
    };
  };

  /**
   * Scrittura in blocco nella bozza (usata dal Quick Order): per gli id passati la
   * quantita' SOSTITUISCE l'esistente (re-import idempotente), il resto della bozza
   * resta. `clear` svuota prima tutto.
   */
  window.OKKO.draftWrite = (list, { clear = false, allowZero = false } = {}) => {
    const draft = clear ? {} : loadDraft();
    list.forEach((e) => {
      if (!e.id) return;
      // allowZero: q=0 TOGLIE la riga dalla bozza (stepper dei Salvati), invece
      // di essere ignorata come nell'import Quick Order.
      if (!(e.q > 0)) {
        if (allowZero) delete draft[e.id];
        return;
      }
      draft[e.id] = {
        q: e.q, p: e.p, c: e.c === undefined ? null : e.c, op: e.op == null ? null : e.op,
        m: e.m === undefined ? null : e.m, cf: e.cf || 0, pre: e.pre || 0,
        t: e.t || '', v: e.v || '', s: e.s || '', i: e.i || '', u: e.u || '', n: e.n || '',
      };
    });
    saveDraft(draft);
    restoreDraft();
  };

  // --- Voce "Passo quantita'" (sidebar) --------------------------------------
  // Voce come le altre: icona + etichetta + badge con lo scatto attivo, cosi'
  // a barra ridotta resta l'icona col badge. Il click cicla tra gli scatti.

  const scattiDi = (voce) => (voce.dataset.okkoPassoScatti || '1')
    .split(',').map((n) => Number(n.trim()) || 1).filter((n) => n > 0);

  function dipingiPasso(voce) {
    const valore = passo();
    const badge = voce.querySelector('[data-okko-passo-valore]');
    if (badge) badge.textContent = String(valore);
    const etichetta = voce.querySelector('.okko-nav__etichetta')?.textContent.trim() || '';
    voce.setAttribute('title', `${etichetta}: ${valore} ${T.pz || 'pz'}`);
    voce.setAttribute('aria-label', `${etichetta}: ${valore} ${T.pz || 'pz'}`);
  }

  document.addEventListener('click', (e) => {
    const voce = e.target.closest('[data-okko-passo-ciclo]');
    if (!voce) return;
    const scatti = scattiDi(voce);
    const i = scatti.indexOf(passo());
    const prossimo = scatti[(i + 1) % scatti.length] || scatti[0];
    try { localStorage.setItem(PASSO_KEY, String(prossimo)); } catch { /* ok */ }
    dipingiPasso(voce);
  });

  document.addEventListener('DOMContentLoaded', () => {
    restoreDraft();
    // lo scatto e' per-dispositivo: al load il badge riprende il valore salvato
    document.querySelectorAll('[data-okko-passo-ciclo]').forEach(dipingiPasso);
  });
  // bfcache: tornare indietro dal browser non rilancia DOMContentLoaded.
  window.addEventListener('pageshow', (e) => { if (e.persisted) restoreDraft(); });
})();
